import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import EffectsBanner from 'components/EffectsBanner';
import OOOBanner from 'components/OOOBanner';
import ProductAddToBag from 'components/ProductAddToBag';
import ProductBrand from 'components/ProductBrand';
import ProductCannabis from 'components/ProductCannabis';
import ProductDetails from 'components/ProductDetails';
import ProductHero from 'components/ProductHero';
import ProductPrice from 'components/ProductPrice';
import ProductTerpenes from 'components/ProductTerpenes';
import ProductVariants from 'components/ProductVariants';
import SurfBanner from 'components/Surfside/SurfBanner';
import useSurfsideViewProduct from 'hooks/useSurfsideViewProduct';
import { addFavorite, removeFavorite } from 'stores/actions/favorite-products';
import formatStoreName from 'utils/format-store-name';
import getVariantSelectionBreakdown from 'utils/get-variant-selection-breakdown';
import { sendEcommerceEvent } from 'utils/metrics';
import percentOff from 'utils/percent-off';
import styles from './ProductLayout.module.scss';

const FAVORITE_ERROR_MESSAGE = 'Please login to save favorite products.';

const ProductLayout = ({
  allVariantsOOS,
  backInStockText,
  badgeImages,
  category,
  closeoutText,
  defaultProductImageHeight,
  defaultProductImageWidth,
  defaultProductImageUrl,
  effectsBanners,
  featuredProductText,
  groupedDiscountsContent,
  newProductText,
  noStoreSelected,
  packagedDateConfiguration,
  packagedDateTooltipInfo,
  priceDropText,
  priceLabel,
  productData: { url, variants },
  salesTaxDisclosure,
  selectedMenu,
  someVariantsOOS,
  storeClosed,
  storeClosedMessage,
  storeSelection,
  terpeneLabels,
  terpeneTooltipInfo,
  variantSKU,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const favoriteSkus = useSelector((state) => state.favoriteProducts);
  const showSurfsideAds = useSelector(
    (state) => state.featureFlags.surfside_ads
  );

  const [variantBreakdown, setVariantBreakdown] = useState(
    getVariantSelectionBreakdown(variantSKU, variants)
  );

  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const email = useSelector((state) => state.user.userInfo.email);
  const isEnrolled = useSelector(
    (state) => state.user.userInfo.isEnrolledMarketing
  );

  const [showFavoriteError, setShowFavoriteError] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  useSurfsideViewProduct({
    productUrl: url,
    variant: variantBreakdown.selectedVariant,
  });

  const handleToggleFavorite = useCallback(() => {
    if (!isLoggedIn) {
      setShowFavoriteError(true);
      return;
    }

    const { SKU } = variantBreakdown.selectedVariant;

    if (SKU) {
      if (isFavorite) {
        dispatch(removeFavorite({ SKU }));
      } else {
        dispatch(addFavorite({ SKU }));
      }
    }
  }, [isFavorite, isLoggedIn, dispatch, variantBreakdown.selectedVariant]);

  useEffect(() => {
    const { SKU } = variantBreakdown.selectedVariant;
    if (SKU) {
      setIsFavorite(favoriteSkus?.includes(SKU));
    }
  }, [favoriteSkus, variantBreakdown.selectedVariant]);

  useEffect(() => {
    setVariantBreakdown(getVariantSelectionBreakdown(variantSKU, variants));
  }, [variants, variantSKU, storeSelection]);

  const handleSelectedVariant = (variant) => {
    // eslint-disable-next-line camelcase
    router.query.variant_sku = variant.SKU;
    router.replace(router, undefined, { shallow: true });
    setVariantBreakdown({ ...variantBreakdown, selectedVariant: variant });
  };

  const {
    availableQuantity,
    batchSKU,
    images,
    discountedPricePerQuantity: {
      discountedPrice: { centAmount: discountedCents } = {},
    } = {},
    price: { centAmount: fullPriceCents, currencyCode } = {},
    SKU,
    tags,
    variationLabel,
    inStock,
    inventoryStatus,
    brand,
    brandDescription,
    classification,
    details,
    effect,
    collection,
    lineage,
    packagedDate,
    name: productName,
    subcategory,
    terpenes,
    availableDiscounts,
    variation,
  } = variantBreakdown.selectedVariant;

  useEffect(() => {
    const enrolledEmail = isEnrolled ? email : undefined;
    const currentPriceAmount = Number(
      ((discountedCents || fullPriceCents) / 100).toFixed(2)
    );
    const gtmData = {
      email: enrolledEmail,
      currencyCode,
      value: currentPriceAmount,
      items: [
        {
          /* eslint-disable camelcase */
          item_brand: brand,
          item_category: category,
          item_id: SKU,
          item_name: productName,
          /* eslint-enable camelcase */
        },
      ],
    };
    const formattedStoreName = formatStoreName(storeSelection);
    if (formattedStoreName) {
      // eslint-disable-next-line camelcase
      gtmData.items[0].location_id = formattedStoreName;
    }
    sendEcommerceEvent('view_item', gtmData);
  }, [SKU, brand, category, email, isEnrolled, productName, storeSelection]);

  const discountPercent = percentOff(fullPriceCents, discountedCents);

  const parsedAvailableQuantity = Number.parseInt(availableQuantity, 10);

  return (
    <div
      id="layout-wrapper"
      className={`px-0 px-xl-20 pt-lg-6 mx-auto ${styles.mw_1500}`}
    >
      <div id="product-info-wrapper" className="d-flex flex-column flex-lg-row">
        <div id="hero-wrapper" className={`${styles.heroWrapper}`}>
          <ProductHero
            backInStockText={backInStockText}
            badgeImages={badgeImages}
            category={category}
            closeoutText={closeoutText}
            collection={collection}
            defaultProductImageHeight={defaultProductImageHeight}
            defaultProductImageWidth={defaultProductImageWidth}
            defaultProductImageUrl={defaultProductImageUrl}
            discountPercent={discountPercent}
            effect={effect}
            featuredProductText={featuredProductText}
            images={images}
            inventoryStatus={inventoryStatus}
            newProductText={newProductText}
            priceDropText={priceDropText}
            productName={productName}
            SKU={SKU}
            tags={tags}
            storeSelection={storeSelection}
          />
        </div>
        <div id="details-wrapper" className="px-10 px-xl-0 w-100">
          {showFavoriteError && (
            <span className="text-red fw-600">{FAVORITE_ERROR_MESSAGE}</span>
          )}
          <ProductVariants
            brand={brand}
            category={category}
            handleToggleFavorite={handleToggleFavorite}
            isFavorite={isFavorite}
            productName={productName}
            selectedSKU={SKU}
            setSelectedVariant={handleSelectedVariant}
            storeSelection={storeSelection}
            variants={variantBreakdown.activeVisbleVariants}
            variationLabel={variationLabel}
          />
          {inStock &&
          variantBreakdown.activeVisbleVariants?.[0] &&
          storeSelection &&
          !storeClosed ? (
            <>
              <ProductCannabis
                category={category}
                classification={classification}
                effect={effect}
                selectedVariant={variantBreakdown.selectedVariant}
                packagedDateConfiguration={packagedDateConfiguration}
                packagedDateTooltipInfo={packagedDateTooltipInfo}
                packagedDate={packagedDate}
                terpeneTooltipInfo={terpeneTooltipInfo}
              />
              <ProductPrice
                availableDiscounts={availableDiscounts}
                discountedCents={discountedCents}
                fullPriceCents={fullPriceCents}
                groupedDiscountsContent={groupedDiscountsContent}
                menu={selectedMenu}
                priceLabel={priceLabel}
              />
              <ProductAddToBag
                batchSKU={batchSKU}
                brand={brand}
                category={category}
                currencyCode={currencyCode}
                currentPriceCents={discountedCents || fullPriceCents}
                productName={productName}
                quantityAvailable={parsedAvailableQuantity}
                regularPriceCents={fullPriceCents}
                SKU={SKU}
                salesTaxDisclosure={salesTaxDisclosure}
                selectedMenu={selectedMenu}
                sourceId="product_detail_page"
                sourceName="Product Detail Page"
                storeSelection={storeSelection}
                subcategory={subcategory}
                variation={variation}
              />
            </>
          ) : (
            <OOOBanner
              allVariantsOOS={allVariantsOOS}
              anyActiveVariants={variantBreakdown.activeVisbleVariants[0]}
              category={category}
              inStockSomeVariant={variantBreakdown.inStockActiveVisibleVariant}
              noStoreSelected={noStoreSelected}
              storeClosed={storeClosed}
              storeClosedMessage={storeClosedMessage}
              storeSelection={storeSelection}
              someVariantsOOS={someVariantsOOS}
            />
          )}

          <ProductDetails
            anyActiveVariants={variantBreakdown.activeVisbleVariants[0]}
            details={details}
            lineage={lineage}
          />
        </div>
      </div>
      <div className="px-10 px-xl-0 pt-14 pt-lg-30">
        <SurfBanner
          className="surf-banner-container"
          showBanner={showSurfsideAds}
          zoneId="0bn9b"
        />
        <ProductTerpenes terpeneLabels={terpeneLabels} terpenes={terpenes} />
        {collection || effect ? (
          <EffectsBanner
            badgeImages={badgeImages}
            category={category}
            collection={collection}
            effect={effect}
            effectsBanners={effectsBanners}
          />
        ) : null}
      </div>
      <div className="px-10 px-xl-0" data-value="product-description-section">
        <ProductBrand brand={brand} brandDescription={brandDescription} />
      </div>
    </div>
  );
};

export default ProductLayout;
